import {
  Box,
  CardContent,
  Card,
  Divider,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  DialogContent,
  Drawer
} from '@mui/material'

import { IUser, Activity } from '@/@data'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useRouteQuery, useTranslator } from '@/hooks'
import CustomAvatar from '@/@core/components/mui/Avatar'
import { getInitials } from '@/utils/getInitials'
import FillterMonthLog from './FillterMonthLog'
import { TablePagination } from '@/@core/components/mui/TablePagination'
import { useState, useMemo } from 'react'

interface AppLogsProps {
  open: boolean
  setOpen: (open: boolean) => void
  onClose?: () => void
  objectId?: any
  object_type: string
  title: string
  companyId?: number
}

export default function AppLogs({ open, setOpen, objectId, object_type, title, companyId }: AppLogsProps) {
  const { __ } = useTranslator()
  const { searchQuery, updateQuery } = useRouteQuery()
  // const [selectedTypes] = useState<string[]>(Object.values(StatusLogsEnum))

  // Local state for drawer-specific parameters
  const [page, setPage] = useState(1)
  const perPage = 10
  const [keyword, setKeyword] = useState('')

  const paramsLogs = useMemo(() => {
    return {
      month: searchQuery.get('month'),
      object_type: object_type,
      object_id: objectId,
      company_id: companyId,
      // type: selectedTypes.length > 0 ? selectedTypes : Object.values(StatusLogsEnum),
      page: page.toString(),
      perPage: perPage.toString(),
      keyword: keyword
    }
  }, [searchQuery, page, perPage, keyword])

  const { data: logs } = useQuery({
    ...Activity.getActivityLogs(paramsLogs),
    enabled: true
  })

  const getAvatar = (params: Pick<IUser, 'avatar_file' | 'first_name'>) => {
    const { avatar_file, first_name } = params

    if (avatar_file) {
      return <CustomAvatar src={avatar_file.tiny_url} skin='light' size={34} />
    } else {
      return (
        <CustomAvatar skin='light' size={34}>
          {getInitials(first_name as string)}
        </CustomAvatar>
      )
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      closeAfterTransition={false}
      anchor='right'
      variant='temporary'
      sx={{ '& .MuiDrawer-paper': { width: 720 } }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 4, mx: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography variant='h5'>{title}</Typography>
        </Box>
        <IconButton size='small' onClick={handleClose}>
          <i className='tabler-x text-2xl text-textPrimary' />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent className=''>
        <FillterMonthLog
          onDateChange={month => {
            updateQuery({ month })
          }}
          status={''}
        />

        <Card className='rounded-[32px] mt-6 border border-[#E6E8EC]'>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                placeholder={__('Search User')}
                value={keyword}
                variant='standard'
                fullWidth
                onChange={e => {
                  setKeyword(e.target.value)
                  setPage(1) // Reset to first page when searching
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start'>
                        <i className='tabler-search' style={{ fontSize: '24px' }} />
                      </InputAdornment>
                    )
                  }
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    padding: '8px 0px'
                  }
                }}
              />
            </Box>

            <div className='mt-6 mb-4'>
              {logs?.data.map((log, index) => (
                <div
                  key={index}
                  className='flex items-center justify-between gap-2 rounded-[24px] p-3 mb-4 last:mb-0 border border-[#E6E8EC]'
                >
                  <div className='flex items-center gap-2 w-[70%]'>
                    <div>
                      <div className='text-[18px] font-medium capitalize text-[#2F2B3DE5]'>{log.type || '-'}</div>
                      <div className='text-[15px] font-normal text-[#2F2B3D66]'>{log.message || '-'}</div>
                    </div>
                  </div>
                  <div className='flex flex-col items-end gap-2 w-[30%] justify-end'>
                    {/* <div className='w-[10px] h-[10px] rounded-full bg-[#ff9f43]'></div> */}
                    <div className='flex items-center gap-2'>
                      <div className='text-[15px] font-medium text-[#2F2B3DE5]'>
                        {log?.causer?.first_name || __('SYSTEM')}
                      </div>
                      <div>
                        {getAvatar({
                          avatar_file: log?.causer?.avatar_file,
                          first_name: log?.causer?.first_name || log?.causer?.employee_name || 'SYSTEM'
                        })}
                      </div>
                    </div>
                    <div className='text-[15px] font-normal text-[#2F2B3D66]'>
                      {dayjs(log.created_at).format('DD/MM/YYYY HH:mm')}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <TablePagination
              meta={{
                total: logs?.meta?.total || 0,
                perPage: logs?.meta?.perPage || 10,
                page: page
              }}
              toggleShow={false}
              onPageChange={setPage}
            />
          </CardContent>
        </Card>
      </DialogContent>
    </Drawer>
  )
}
